import Header from "../../../shared/app-shell/Header";
import Sidebar from "../../../shared/app-shell/Sidebar";
import { themeAtom } from "../../../shared/atoms";
import HullCleaningCaseFilters from "../components/HullCleaningCaseFilters";
import HullCleaningCaseModal from "../components/HullCleaningCaseModal";
import HullCleaningCasesTable from "../components/HullCleaningCasesTable";
import HullCleaningFilterPane from "../components/HullCleaningFilterPane";
import HullCleaningHistoryTables from "../components/HullCleaningHistoryTables";
import HullCleaningVoyageTable from "../components/HullCleaningVoyageTable";
import { useHullCleaningDashboard } from "../state/useHullCleaningDashboard";
import { WrenchScrewdriverIcon } from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { twMerge } from "tailwind-merge";

// Replaces the live-connected Power BI report with three bookmark-equivalent OpsLink views.
// The page keeps its own dashboard state so routing remains a simple access and page selection boundary.
const HullCleaningFollower = () => {
  const theme = useAtomValue(themeAtom);
  const state = useHullCleaningDashboard(true);

  return (
    <div className="flex h-screen" data-test="hull-cleaning-dashboard-page">
      <Sidebar />
      <div
        className={twMerge(
          "flex min-w-0 flex-1 flex-col overflow-hidden",
          theme.home.tailwind.backgound,
          theme.home.tailwind.textDefault,
        )}
      >
        <Header />
        <main className="flex-1 overflow-y-auto px-4 pb-6 pt-3 sm:px-6">
          <div className="mx-auto max-w-[1900px]">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-20 text-green dark:bg-teal-900/50">
                  <WrenchScrewdriverIcon className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-green">Hull cleaning</p>
                  <h1 className="text-xl font-semibold sm:text-2xl">Cargill UWI/C Follower</h1>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span
                  className={twMerge(
                    "rounded-full px-3 py-1 text-xs font-semibold",
                    state.useMockData ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800",
                  )}
                >
                  {state.useMockData ? "Mock data" : "Snowflake data"}
                </span>
                {state.view === "cases" && (
                  <div
                    data-test="hull-cleaning-request-count"
                    className="rounded-xl border border-primary-20 bg-primary-10 px-5 py-2 text-right dark:border-teal-800 dark:bg-teal-950/40"
                  >
                    <p className="text-2xl font-semibold text-green">{state.filtered.cases.length}</p>
                    <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500">Total requests</p>
                  </div>
                )}
              </div>
            </div>
            {state.isLoading && (
              <div className="rounded-xl border bg-white p-10 text-center text-sm text-gray-500 dark:bg-gray-800">
                Loading hull-cleaning dashboard…
              </div>
            )}
            {state.error && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700" role="alert">
                {state.error} Use the sidebar mock-data toggle if you need to review the UI without Snowflake.
              </div>
            )}
            {state.dashboard && !state.isLoading && (
              <div className="grid items-start gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
                <HullCleaningFilterPane
                  dashboard={state.dashboard}
                  filters={state.filters}
                  view={state.view}
                  setFilter={state.setFilter}
                  clearFilters={state.clearFilters}
                  setView={state.setView}
                />
                <section className="min-w-0 space-y-4">
                  {state.view === "voyage" && (
                    <HullCleaningVoyageTable
                      rows={state.filtered.voyageItinerary}
                      caseImos={state.caseImos}
                      onSelectCaseForImo={state.selectCaseForImo}
                    />
                  )}
                  {state.view === "cases" && (
                    <>
                      <HullCleaningCaseFilters
                        rows={state.dashboard.cases}
                        filters={state.filters}
                        setFilter={state.setFilter}
                      />
                      <HullCleaningCasesTable rows={state.filtered.cases} onSelect={state.setSelectedCase} />
                    </>
                  )}
                  {state.view === "history" && (
                    <HullCleaningHistoryTables
                      dryDocks={state.filtered.dryDocks}
                      hullEvents={state.filtered.hullEvents}
                    />
                  )}
                </section>
              </div>
            )}
          </div>
        </main>
      </div>
      {state.selectedCase && (
        <HullCleaningCaseModal selectedCase={state.selectedCase} onClose={() => state.setSelectedCase(null)} />
      )}
    </div>
  );
};

export default HullCleaningFollower;
